import React, { FunctionComponent, useState } from 'react'

import styled from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { ToggleSwitch } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/toggleSwitch/ToggleSwitch'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { clearDevFlagOverrides, readDevFlagOverrides, writeDevFlagOverrides } from './devFlagOverrides'
import { useDevMocks } from './DevMocksContext'
import { NpTestPlanDevMocks } from './devMocksTypes'

export interface DevMocksModalProps {
  visible: boolean
  onRequestClose: () => void
}

const Content = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['24px'],
  paddingVertical: spacing['8px'],
}))

const Section = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const Row = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing['16px'],
}))

const RowText = styled.View({
  flex: 1,
})

const Actions = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: spacing['8px'],
  paddingTop: spacing['8px'],
}))

/** The gate is three-valued: the mock can be absent, or pin it open or shut. */
enum IntakeNoteOption {
  REAL = 'real',
  COMPLETED = 'completed',
  NOT_COMPLETED = 'notCompleted',
}

const INTAKE_NOTE_OPTIONS = [
  { value: IntakeNoteOption.REAL, label: 'Use the real notes' },
  { value: IntakeNoteOption.COMPLETED, label: 'Completed — build form' },
  { value: IntakeNoteOption.NOT_COMPLETED, label: 'Not completed — waiting state' },
]

const toIntakeNoteOption = (intakeNoteCompleted?: boolean) => {
  if (intakeNoteCompleted === undefined) {
    return IntakeNoteOption.REAL
  }
  return intakeNoteCompleted ? IntakeNoteOption.COMPLETED : IntakeNoteOption.NOT_COMPLETED
}

const fromIntakeNoteOption = (option: IntakeNoteOption) => {
  switch (option) {
    case IntakeNoteOption.REAL:
      return undefined
    case IntakeNoteOption.COMPLETED:
      return true
    case IntakeNoteOption.NOT_COMPLETED:
      return false
  }
}

interface ToggleRowProps {
  label: string
  description: string
  isOn: boolean
  onPress: (on: boolean) => void
  testID?: string
}

const ToggleRow: FunctionComponent<ToggleRowProps> = ({ label, description, isOn, onPress, testID }) => (
  <Row>
    <RowText>
      <BodyText text={label} size={BodyTextSize.DEFAULT} />
      <BodyText text={description} size={BodyTextSize.CAPTION} />
    </RowText>
    <ToggleSwitch testID={testID} isOn={isOn} onPress={onPress} accessibilityLabel={label} />
  </Row>
)

/**
 * [ENGBEND-12942] Dev-only panel. Mocked data goes to `DevMocksContext` and applies immediately;
 * real LaunchDarkly flags go to the flag override store, which is only read at startup and so
 * needs a reload.
 */
export const DevMocksModal: FunctionComponent<DevMocksModalProps> = ({ visible, onRequestClose }) => {
  const { devMocks, setDevMocks, resetDevMocks } = useDevMocks()
  const [storedFlags] = useState(readDevFlagOverrides)

  const [npTestingPlans, setNpTestingPlans] = useState(Boolean(storedFlags.npTestingPlans))
  const [clientHeaderRedesign, setClientHeaderRedesign] = useState(Boolean(storedFlags.isClientHeaderRedesignEnabled))
  const [npTestPlan, setNpTestPlan] = useState<NpTestPlanDevMocks>(devMocks.npTestPlan)

  const updateNpTestPlan = (changes: Partial<NpTestPlanDevMocks>) =>
    setNpTestPlan((current) => ({ ...current, ...changes }))

  const onApply = () => {
    setDevMocks({ ...devMocks, npTestPlan })

    const haveFlagsChanged =
      Boolean(storedFlags.npTestingPlans) !== npTestingPlans ||
      Boolean(storedFlags.isClientHeaderRedesignEnabled) !== clientHeaderRedesign
    writeDevFlagOverrides({
      npTestingPlans: npTestingPlans || undefined,
      isClientHeaderRedesignEnabled: clientHeaderRedesign || undefined,
    })

    if (haveFlagsChanged) {
      window.location.reload()
      return
    }
    onRequestClose()
  }

  const onReset = () => {
    resetDevMocks()
    clearDevFlagOverrides()
    window.location.reload()
  }

  return (
    <Modal
      visible={visible}
      onRequestClose={onRequestClose}
      width='520px'
      modalTitle='Dev mocks'
      modalContents={
        <Content testID={tID('DevMocksModal')}>
          <Section>
            <BodyText bold text='Feature flags' size={BodyTextSize.DEFAULT} />
            <BodyText
              size={BodyTextSize.SMALL}
              text='Real LaunchDarkly flags, overridden in localStorage. Changing one reloads the page.'
            />
            <ToggleRow
              testID={tID('DevMocksModal-npTestingPlans')}
              label='NP test plans'
              description='Feature flag np-testing-plans. Required for anything below to take effect.'
              isOn={npTestingPlans}
              onPress={setNpTestingPlans}
            />
            <ToggleRow
              testID={tID('DevMocksModal-clientHeaderRedesign')}
              label='Redesigned client header'
              description='Feature flag from another rollout. The NP test plan designs assume it is on.'
              isOn={clientHeaderRedesign}
              onPress={setClientHeaderRedesign}
            />
          </Section>
          <Section>
            <BodyText bold text='NP test plan mocks' size={BodyTextSize.DEFAULT} />
            <BodyText size={BodyTextSize.SMALL} text='Stands in for data the backend cannot serve yet.' />
            <ToggleRow
              testID={tID('DevMocksModal-forceRole')}
              label='Treat me as a Neuropsych provider'
              description='The entrypoint is only for the Psychologist / Neuropsychologist roles.'
              isOn={npTestPlan.forceRole}
              onPress={(forceRole) => updateNpTestPlan({ forceRole })}
            />
            <ToggleRow
              testID={tID('DevMocksModal-forceEpisode')}
              label='Treat selected episode as Neuropsych'
              description='Shows the Evaluation tab on any episode, since NP episodes cannot be created yet.'
              isOn={npTestPlan.forceEpisode}
              onPress={(forceEpisode) => updateNpTestPlan({ forceEpisode })}
            />
            <SelectField
              useStringValue
              label='Intake note'
              name='devMocksIntakeNote'
              value={toIntakeNoteOption(npTestPlan.intakeNoteCompleted)}
              onChange={(value) =>
                updateNpTestPlan({ intakeNoteCompleted: fromIntakeNoteOption(value as IntakeNoteOption) })
              }
              options={INTAKE_NOTE_OPTIONS}
            />
          </Section>
          <Actions>
            <TertiaryButton testID={tID('DevMocksModal-reset')} text='Clear all' onPress={onReset} />
            <PrimaryButton testID={tID('DevMocksModal-apply')} text='Apply' onPress={onApply} />
          </Actions>
        </Content>
      }
    />
  )
}
